/**
 * Vendor Bot Admin - Customers Page
 * Customer database with search and metrics
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import DataTable from '../../components/DataTable';
import { useTelegram } from '../../hooks/useTelegram';

const SearchBar = styled.input`
  width: 100%;
  padding: var(--spacing-md);
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--font-md);
  margin-bottom: var(--spacing-lg);

  &:focus {
    outline: none;
    border-color: var(--accent-green);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const StatCard = styled(Card)`
  text-align: center;
  padding: var(--spacing-md);
`;

const StatValue = styled.div`
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
`;

const StatLabel = styled.div`
  font-size: var(--font-xs);
  color: var(--text-secondary);
`;

const Badge = styled.span<{ color?: string }>`
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  font-size: var(--font-xs);
  font-weight: var(--font-medium);
  background: ${({ color }) => color || 'rgba(142, 142, 147, 0.2)'};
  color: var(--text-primary);
`;

const Customers: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { showBackButton } = useTelegram();
  const [searchQuery, setSearchQuery] = useState('');
  const [customers] = useState([
    {
      id: 1,
      name: 'John Doe',
      username: '@johndoe',
      orders: 5,
      totalSpent: 249.95,
      lastOrder: 'Oct 6, 2025',
    },
    {
      id: 2,
      name: 'Jane Smith',
      username: '@janesmith',
      orders: 12,
      totalSpent: 589.88,
      lastOrder: 'Oct 5, 2025',
    },
    {
      id: 3,
      name: 'Bob Wilson',
      username: '@bobwilson',
      orders: 3,
      totalSpent: 149.97,
      lastOrder: 'Oct 3, 2025',
    },
  ]);

  useEffect(() => {
    showBackButton(() => navigate(`/vendor/${botUsername}/admin/dashboard`));
  }, [showBackButton, navigate, botUsername]);

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      key: 'name',
      label: 'Customer',
      render: (value: string, row: any) => (
        <div>
          <div style={{ fontWeight: 'var(--font-medium)' }}>{value}</div>
          <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {row.username}
          </div>
        </div>
      ),
    },
    {
      key: 'orders',
      label: 'Orders',
      render: (value: number) => (
        <Badge color={value > 10 ? 'rgba(76, 175, 80, 0.2)' : undefined}>
          {value}
        </Badge>
      ),
    },
    {
      key: 'totalSpent',
      label: 'Total',
      render: (value: number) => (
        <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--accent-green)' }}>
          ${value.toFixed(2)}
        </span>
      ),
    },
  ];

  const totalCustomers = customers.length;
  const totalRevenue = customers.reduce((sum, c) => sum + c.totalSpent, 0);
  const avgOrderValue = totalRevenue / customers.reduce((sum, c) => sum + c.orders, 0);

  return (
    <Layout header={<Navbar title="Customers" showBack />}>
      <StatsGrid>
        <StatCard>
          <StatValue>{totalCustomers}</StatValue>
          <StatLabel>Total</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>${totalRevenue.toFixed(0)}</StatValue>
          <StatLabel>Revenue</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>${avgOrderValue.toFixed(0)}</StatValue>
          <StatLabel>Avg Order</StatLabel>
        </StatCard>
      </StatsGrid>

      <SearchBar
        type="text"
        placeholder="Search customers..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <DataTable columns={columns} data={filteredCustomers} emptyMessage="No customers found" />
    </Layout>
  );
};

export default Customers;
